import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { api } from '../services/authService';
import { CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import Modal from '../components/ui/Modal';

const UploadPollingContext = createContext(null);

export function UploadPollingProvider({ children }) {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [uploadJobIdToPoll, setUploadJobIdToPoll] = useState(null);
  const autoReportOpenedForExecutionIdRef = useRef(null);
  /** @type {React.MutableRefObject<{ executionId: string, variant: string } | null>} */
  const pendingAutoReportRef = useRef(null);

  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [reportError, setReportError] = useState(null);

  useEffect(() => {
    if (!currentUser) {
      setUploadJobIdToPoll(null);
      autoReportOpenedForExecutionIdRef.current = null;
      pendingAutoReportRef.current = null;
      setReportModalOpen(false);
      setReportData(null);
      setReportError(null);
    }
  }, [currentUser]);

  const { data: uploadJobStatus } = useQuery({
    queryKey: ['uploadPollingStatus', uploadJobIdToPoll],
    queryFn: async () => {
      const res = await api.get('/api/v2/upload_status', {
        params: { execution_id: uploadJobIdToPoll },
      });
      return res.data;
    },
    enabled: Boolean(currentUser && uploadJobIdToPoll),
    refetchInterval: (query) => {
      const st = query.state.data?.execution_status;
      if (st === 'complete' || st === 'error') return false;
      return 30000;
    },
  });

  const openProcessingReport = useCallback(async (executionId, options = {}) => {
    if (!executionId) return;
    setReportModalOpen(true);
    setReportError(null);
    setReportData(null);
    setReportLoading(true);
    try {
      const reportPath = options.admin
        ? '/api/v2/admin/file_upload_report'
        : '/api/v2/file_upload_report';
      const res = await api.get(reportPath, {
        params: { execution_id: executionId },
      });
      setReportData(res.data);
    } catch (error) {
      setReportError(
        error.response?.data?.detail || error.message || 'Failed to load report.'
      );
    } finally {
      setReportLoading(false);
    }
  }, []);

  const startUploadJobPolling = useCallback((executionId, variant = 'file_upload') => {
    if (!executionId) return;
    autoReportOpenedForExecutionIdRef.current = null;
    pendingAutoReportRef.current = { executionId, variant };
    setUploadJobIdToPoll(executionId);
  }, []);

  useEffect(() => {
    if (!uploadJobIdToPoll || !uploadJobStatus?.execution_status) return;
    const s = uploadJobStatus.execution_status;
    if (s !== 'complete' && s !== 'error') return;
    if (autoReportOpenedForExecutionIdRef.current === uploadJobIdToPoll) return;
    autoReportOpenedForExecutionIdRef.current = uploadJobIdToPoll;
    const id = uploadJobIdToPoll;
    const pending = pendingAutoReportRef.current;
    const variant =
      pending && pending.executionId === id ? pending.variant : 'file_upload';
    pendingAutoReportRef.current = null;
    setUploadJobIdToPoll(null);
    if (variant === 'fast_id') {
      // FastID reports live on their own full-screen page rather than the report modal.
      navigate(`/fastid-report/${id}`);
    } else {
      void openProcessingReport(id);
    }
  }, [uploadJobIdToPoll, uploadJobStatus?.execution_status, openProcessingReport, navigate]);

  const closeReportModal = useCallback(() => {
    setReportModalOpen(false);
    setReportData(null);
    setReportError(null);
  }, []);

  const value = useMemo(
    () => ({
      startUploadJobPolling,
      openProcessingReport,
      reportModalOpen,
    }),
    [startUploadJobPolling, openProcessingReport, reportModalOpen]
  );

  return (
    <UploadPollingContext.Provider value={value}>
      {children}
      <Modal
        isOpen={reportModalOpen}
        onClose={closeReportModal}
        className="w-full max-h-[90vh] overflow-y-auto max-w-3xl"
        zIndex="z-[100]"
      >
        <CardHeader className="pr-10">
          <CardTitle>Processing report</CardTitle>
        </CardHeader>
        <CardContent>
          {reportLoading && <p className="text-sm text-gray-600">Loading report…</p>}
          {!reportLoading && reportError && <p className="text-sm text-red-600">{reportError}</p>}
          {!reportLoading && !reportError && reportData && (
            <div className="text-sm text-gray-800 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                {[
                  ['num_rows', 'Rows (total)'],
                  ['valid_rows', 'Valid rows'],
                  ['failed_rows', 'Failed rows'],
                  ['duplicate_rows', 'Duplicate rows'],
                  ['inserted_rows', 'Inserted rows'],
                ].map(([key, label]) => (
                  <div key={key}>
                    <span className="font-medium text-gray-700">{label}:</span>{' '}
                    <span>{reportData[key] != null ? String(reportData[key]) : '—'}</span>
                  </div>
                ))}
              </div>
              {reportData.execution_id && (
                <div>
                  <span className="font-medium text-gray-700">Execution ID:</span>{' '}
                  <code className="break-all bg-gray-100 px-2 py-0.5 rounded text-xs">
                    {reportData.execution_id}
                  </code>
                </div>
              )}
              {reportData.error_message && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Error message</h4>
                  <pre className="text-xs whitespace-pre-wrap bg-red-50 border border-red-100 rounded p-2 text-red-900">
                    {reportData.error_message}
                  </pre>
                </div>
              )}
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Result preview (JSON)</h4>
                <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-3 overflow-auto max-h-64">
                  {reportData.result_json != null
                    ? JSON.stringify(reportData.result_json, null, 2)
                    : '—'}
                </pre>
              </div>
            </div>
          )}
        </CardContent>
      </Modal>
    </UploadPollingContext.Provider>
  );
}

export function useUploadPolling() {
  const ctx = useContext(UploadPollingContext);
  if (!ctx) {
    throw new Error('useUploadPolling must be used within UploadPollingProvider');
  }
  return ctx;
}
